import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearch, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useBoard } from "@/hooks/useBoard";
import { useUndoStack } from "@/hooks/useUndoStack";
import { showToast } from "@/lib/toast";
import { BoardSurface } from "@/components/board/BoardSurface";
import type { CopyRowOpts } from "@/components/board/RowCopyMenu";
import { NewTicketDialog } from "@/components/submit/NewTicketDialog";
import { Breadcrumb } from "@/components/ui/Breadcrumb";
import type { BoardView } from "@/components/board/BoardToolbar";
import { LoadingState } from "@/components/common/LoadingState";
import { EmptyState } from "@/components/common/EmptyState";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import {
  adaptBoard,
  buildCellWrite,
  fixtureBoard,
  type ViewBoard,
  type ViewRow,
} from "@/lib/boardView";
import { devFixturesEnabled } from "@/lib/sample-data";
import { t } from "@/lib/strings";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspaceMembers } from "@/hooks/useWorkspaceMembers";
import { withOptionArchived } from "@/lib/groupDelete";
import type {
  BoardDetail,
  CellValue,
  ColumnConfig,
  ColumnDef,
  ColumnUpdatePayload,
  UUID,
} from "@/lib/types";

interface BoardSearch {
  view?: BoardView;
  row?: string;
  comment?: string;
  q?: string;
}

function readRawCell(
  data: BoardDetail | undefined,
  rowId: string,
  columnId: string,
): CellValue | undefined {
  const row = data?.rows.find((r) => r.id === rowId);
  return row?.cells?.find((c) => c.column_def_id === columnId)?.value;
}

// Cell values are JSON-serialisable; compare structurally (null === undefined here).
function sameValue(a: CellValue | undefined, b: CellValue | undefined): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

export function BoardPage() {
  const { boardId } = useParams({ strict: false }) as { boardId: string };
  const search = useSearch({ strict: false }) as BoardSearch;
  const navigate = useNavigate();
  const useFixtures = devFixturesEnabled();
  const board = useBoard(useFixtures ? undefined : boardId);
  const view: BoardView = search.view ?? "table";
  const openRowId = search.row ?? null;
  const openCommentId = search.comment ?? null;
  const openCommentQuery = search.q ?? null;
  // Jump-to-comment (①): once BoardSurface has captured the comment focus, drop
  // `?comment=` from the URL (keep view + row) so a re-render / manual close is
  // clean and can't re-flash. Mirrors how `?row=` open/close is synced.
  const clearComment = useCallback(() => {
    if (!openCommentId) return;
    void navigate({
      to: "/b/$boardId",
      params: { boardId },
      search: { view, row: openRowId ?? undefined },
      replace: true,
    });
  }, [boardId, navigate, openCommentId, view, openRowId]);
  const setView = useCallback(
    (next: BoardView) => {
      void navigate({
        to: "/b/$boardId",
        params: { boardId },
        search: { view: next, row: openRowId ?? undefined },
        replace: true,
      });
    },
    [boardId, navigate, openRowId],
  );
  const setOpenRow = useCallback(
    (rowId: string | null) => {
      if (rowId === openRowId) return;
      void navigate({
        to: "/b/$boardId",
        params: { boardId },
        search: { view, row: rowId ?? undefined },
        replace: true,
      });
    },
    [boardId, navigate, openRowId, view],
  );

  const [fixturePatches, setFixturePatches] = useState<Record<string, Partial<ViewRow>>>({});
  // Just-created row → TableView auto-focuses its title cell + scrolls it in.
  const [focusRowId, setFocusRowId] = useState<UUID | null>(null);
  const fixture = useMemo<ViewBoard | null>(() => {
    if (!useFixtures) return null;
    const f = fixtureBoard();
    if (Object.keys(fixturePatches).length === 0) return f;
    return {
      ...f,
      rows: f.rows.map((r) => (fixturePatches[r.id] ? { ...r, ...fixturePatches[r.id] } : r)),
    };
  }, [useFixtures, fixturePatches]);

  const adapted = useMemo<ViewBoard | null>(() => {
    if (useFixtures) return fixture;
    return board.data ? adaptBoard(board.data) : null;
  }, [useFixtures, fixture, board.data]);

  // Undo/redo: per-board, in-memory command stack. Inverses route through the
  // same optimistic + WS-synced mutations, so collaborators see undo like any
  // edit. dataRef gives the undo closures the *current* board state for the
  // stale-guard (skip the revert if someone changed the cell since).
  const undo = useUndoStack(boardId ?? null);
  const dataRef = useRef<BoardDetail | undefined>(undefined);
  dataRef.current = board.data;

  useEffect(() => {
    if (useFixtures) return;
    function onKey(e: KeyboardEvent) {
      const isZ = e.key.toLowerCase() === "z";
      const isY = e.key.toLowerCase() === "y";
      if (!(e.ctrlKey || e.metaKey) || (!isZ && !isY)) return;
      const el = document.activeElement as HTMLElement | null;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable)) {
        return; // inside a text editor → let native undo win
      }
      e.preventDefault();
      if (isY || (isZ && e.shiftKey)) undo.redo();
      else undo.undo();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [undo, useFixtures]);

  const recordCellEdit = useCallback(
    (rowId: string, columnId: string, prevValue: CellValue | undefined, nextValue: CellValue) => {
      undo.push({
        undo: () => {
          const cur = readRawCell(dataRef.current, rowId, columnId);
          if (!sameValue(cur, nextValue)) {
            showToast(t.undoSkippedChanged);
            return;
          }
          board.updateCell.mutate({
            rowId: rowId as UUID,
            columnDefId: columnId as UUID,
            value: prevValue ?? null,
          });
        },
        redo: () =>
          board.updateCell.mutate({
            rowId: rowId as UUID,
            columnDefId: columnId as UUID,
            value: nextValue,
          }),
      });
    },
    [board, undo],
  );

  const onPatchRow = useCallback(
    (id: string, patch: Partial<ViewRow>) => {
      if (useFixtures || !adapted || !board.data) {
        setFixturePatches((prev) => ({ ...prev, [id]: { ...(prev[id] ?? {}), ...patch } }));
        return;
      }
      // Translate flat-row patch into one cell.write per field.
      for (const [field, value] of Object.entries(patch) as [
        keyof ViewRow,
        ViewRow[keyof ViewRow],
      ][]) {
        const write = buildCellWrite(adapted, field, value);
        if (!write) continue;
        const prevValue = readRawCell(board.data, id, write.columnId);
        if (sameValue(prevValue, write.value)) continue;
        board.updateCell.mutate({ rowId: id, columnDefId: write.columnId, value: write.value });
        recordCellEdit(id, write.columnId, prevValue, write.value);
      }
    },
    [adapted, board, useFixtures, recordCellEdit],
  );

  const onAddRow = useCallback(
    (afterRowId?: UUID | null, inGroup?: { columnId: UUID; value: CellValue }) => {
      if (useFixtures || !board.data) return;
      void board.createRow
        .mutateAsync(afterRowId ? { afterRowId } : undefined)
        .then((row) => {
          setFocusRowId(row.id as UUID);
          // B7: drop the new row into the group it was added from.
          if (inGroup) {
            board.updateCell.mutate({
              rowId: row.id as UUID,
              columnDefId: inGroup.columnId,
              value: inGroup.value,
            });
          }
        })
        .catch(() => {});
    },
    [useFixtures, board],
  );

  const onPatchCell = useCallback(
    (rowId: string, columnDefId: string, value: CellValue) => {
      if (useFixtures || !board.data) return;
      const prevValue = readRawCell(board.data, rowId, columnDefId);
      if (sameValue(prevValue, value)) return;
      board.updateCell.mutate({ rowId: rowId as UUID, columnDefId: columnDefId as UUID, value });
      recordCellEdit(rowId, columnDefId, prevValue, value);
    },
    [useFixtures, board, recordCellEdit],
  );

  const onAddColumnOption = useCallback(
    async (column: ColumnDef, label: string) => {
      // Mutation writes the updated column into the board cache (useBoard.ts),
      // so the new option shows up in the picker immediately.
      const updated = await board.addColumnOption.mutateAsync({ column, label });
      const opts = (updated.config?.options ?? []) as Array<{
        id: string;
        label: string;
        color: string;
      }>;
      return opts[opts.length - 1] ?? null;
    },
    [board],
  );

  const onRowPositionChange = useCallback(
    (rowId: UUID, position: number) => {
      if (useFixtures || !board.data) return;
      const prevPos = board.data.rows.find((r) => r.id === rowId)?.position;
      board.updateRowPosition.mutate({ rowId, position });
      if (prevPos != null && prevPos !== position) {
        undo.push({
          undo: () => board.updateRowPosition.mutate({ rowId, position: prevPos }),
          redo: () => board.updateRowPosition.mutate({ rowId, position }),
        });
      }
    },
    [useFixtures, board, undo],
  );

  const onMoveRow = useCallback(
    (rowId: UUID, parentRowId: UUID | null, position: number) => {
      if (useFixtures || !board.data) return;
      board.moveRow.mutate({ rowId, parentRowId, position });
    },
    [useFixtures, board],
  );

  const onColumnUpdate = useCallback(
    (columnId: UUID, payload: ColumnUpdatePayload) => {
      if (useFixtures || !board.data) return;
      board.updateColumn.mutate({ columnId, payload });
    },
    [useFixtures, board],
  );

  const onAddSubitem = useCallback(
    (parentRowId: UUID) => {
      if (useFixtures || !board.data) return;
      void board.createRow
        .mutateAsync({ parentRowId })
        .then((row) => setFocusRowId(row.id as UUID))
        .catch(() => {});
    },
    [useFixtures, board],
  );

  // Unteraufgaben: mint the child row, then write its title and (if picked) its assignee.
  // Same path as every other write — createRow/updateCell from useBoard, no new plumbing.
  // ONE call from the section so the sub-task is never briefly untitled or unassigned.
  const onCreateSubtask = useCallback(
    (parentRowId: UUID, title: string, assigneeId: string | null) => {
      if (useFixtures || !board.data) return;
      // Sort by position first: "the first text column" must mean the same column the panel
      // and the table call the title (adaptBoard sorts; the raw payload need not be sorted).
      const columns = [...board.data.columns].sort((a, b) => a.position - b.position);
      const titleCol = columns.find((c) => c.type === "text");
      const personCol = columns.find((c) => c.type === "person");
      void board.createRow
        .mutateAsync({ parentRowId })
        .then((created) => {
          // A board with no text column can't hold a title — create the row anyway rather
          // than throw. Same for a board with no person column and a picked assignee.
          if (titleCol) {
            board.updateCell.mutate({
              rowId: created.id as UUID,
              columnDefId: titleCol.id,
              value: title,
            });
          }
          if (assigneeId && personCol) {
            // Person cells are a LIST of user uuids.
            board.updateCell.mutate({
              rowId: created.id as UUID,
              columnDefId: personCol.id,
              value: [assigneeId],
            });
          }
        })
        // 🔴 NEVER swallow this. A bare `.catch(() => {})` is how a 422 (the backend's
        // MAX_ROW_DEPTH cap) stayed invisible: the input cleared, the typed title vanished
        // and the user was told nothing. A failed create must SAY it failed.
        .catch(() => showToast(t.subtaskCreateFailed, "error"));
    },
    [useFixtures, board],
  );

  const onDeleteRow = useCallback(
    (rowId: UUID) => {
      if (useFixtures || !board.data) return;
      // Snapshot the row + its direct children BEFORE deleting so undo can
      // re-add them with full fidelity (cells included).
      const snapshot = board.data.rows.filter((r) => r.id === rowId || r.parent_row_id === rowId);
      if (snapshot.length === 0) return;
      board.deleteRow.mutate({ rowId });
      undo.push({
        undo: () => board.restoreRow.mutate({ rowId, rows: snapshot }),
        redo: () => board.deleteRow.mutate({ rowId }),
      });
    },
    [useFixtures, board, undo],
  );

  // "Gruppe löschen": every top-level row of the group goes through the SAME per-row
  // delete route as a single delete (so its guards hold — e.g. memo-owned rows stay
  // admin-only), sub-items cascade server-side, and the group's status option is
  // archived so pickers stop offering it. One undo step brings rows + option back.
  const onDeleteGroupRows = useCallback(
    (groupColumnId: UUID, optionId: string, rowIds: UUID[]) => {
      if (useFixtures || !board.data || rowIds.length === 0) return;
      const ids = new Set<string>(rowIds);
      const snapshot = board.data.rows.filter(
        (r) => ids.has(r.id) || (r.parent_row_id != null && ids.has(r.parent_row_id)),
      );
      // An option that was ALREADY archived (it can still hold rows) is left alone — and
      // must not be un-archived by the undo either.
      const alreadyArchived =
        (
          (board.data.columns.find((c) => c.id === groupColumnId)?.config ?? {}) as ColumnConfig
        ).options?.find((o) => o.id === optionId)?.archived === true;
      // Read the column at execution time (dataRef), so an undo much later can't write
      // back a stale option list over renames/recolours made since.
      const setArchived = (archived: boolean) => {
        const col = dataRef.current?.columns.find((c) => c.id === groupColumnId);
        if (!col) return;
        const cfg = (col.config ?? {}) as ColumnConfig;
        board.updateColumn.mutate({
          columnId: groupColumnId,
          payload: {
            config: { ...cfg, options: withOptionArchived(cfg.options ?? [], optionId, archived) },
          },
        });
      };
      void Promise.allSettled(rowIds.map((rowId) => board.deleteRow.mutateAsync({ rowId }))).then(
        (results) => {
          const deleted = rowIds.filter((_, i) => results[i]?.status === "fulfilled");
          const failed = rowIds.length - deleted.length;
          if (failed > 0) {
            // Keep the group (some of its rows are still there) and resync: a failed
            // optimistic delete restores its pre-delete snapshot, which can briefly
            // resurrect rows whose delete DID succeed.
            showToast(t.groupDeletePartial(failed), "error");
            void board.refetch();
          } else {
            if (!alreadyArchived) setArchived(true);
            showToast(t.groupDeleted, "success");
          }
          if (deleted.length === 0) return;
          const archivedToo = failed === 0 && !alreadyArchived;
          undo.push({
            undo: () => {
              for (const rowId of deleted) {
                board.restoreRow.mutate({
                  rowId,
                  rows: snapshot.filter((r) => r.id === rowId || r.parent_row_id === rowId),
                });
              }
              if (archivedToo) setArchived(false);
            },
            redo: () => {
              for (const rowId of deleted) board.deleteRow.mutate({ rowId });
              if (archivedToo) setArchived(true);
            },
          });
        },
      );
    },
    [useFixtures, board, undo],
  );

  const onDuplicateRow = useCallback(
    (rowId: UUID, withComments: boolean) => {
      if (useFixtures || !board.data) return;
      board.duplicateRow.mutate({ rowId, withComments });
    },
    [useFixtures, board],
  );

  const onCopyRow = useCallback(
    (rowId: UUID, opts: CopyRowOpts) => {
      if (useFixtures || !board.data) return;
      board.duplicateRow.mutate({ rowId, ...opts });
    },
    [useFixtures, board],
  );

  const onDuplicateGroup = useCallback(
    (groupColumnId: string, sourceValue: unknown, newLabel: string, withComments: boolean) => {
      if (useFixtures || !board.data) return;
      board.duplicateGroup.mutate({
        groupColumnId: groupColumnId as UUID,
        sourceValue,
        newLabel,
        withComments,
      });
    },
    [useFixtures, board],
  );

  if (useFixtures) {
    if (!adapted) return null;
    return (
      <BoardPageInner
        board={adapted}
        rawBoard={null}
        view={view}
        setView={setView}
        onPatchRow={onPatchRow}
        onAddRow={undefined}
        onPatchCell={undefined}
        onAddColumnOption={undefined}
        onRowPositionChange={undefined}
        onMoveRow={undefined}
        onColumnUpdate={undefined}
        onAddSubitem={undefined}
        onCreateSubtask={undefined}
        onDeleteRow={undefined}
        onDuplicateRow={undefined}
        onCopyRow={undefined}
        onDuplicateGroup={undefined}
        onDeleteGroupRows={undefined}
        onUndo={undo.undo}
        onRedo={undo.redo}
        canUndo={undo.canUndo}
        canRedo={undo.canRedo}
        openRowId={openRowId}
        openCommentId={openCommentId}
        openCommentQuery={openCommentQuery}
        onDetailChange={setOpenRow}
        onCommentConsumed={clearComment}
      />
    );
  }

  if (board.isLoading) return <LoadingState />;
  if (board.error || !adapted) {
    return (
      <EmptyState
        title="Board nicht verfügbar"
        description={
          board.error instanceof Error
            ? board.error.message
            : "Dieses Board konnte nicht geladen werden."
        }
      />
    );
  }
  return (
    <BoardPageInner
      board={adapted}
      rawBoard={board.data ?? null}
      view={view}
      setView={setView}
      onPatchRow={onPatchRow}
      onAddRow={onAddRow}
      onPatchCell={onPatchCell}
      onAddColumnOption={onAddColumnOption}
      onRowPositionChange={onRowPositionChange}
      onMoveRow={onMoveRow}
      onColumnUpdate={onColumnUpdate}
      onAddSubitem={onAddSubitem}
      onCreateSubtask={onCreateSubtask}
      onDeleteRow={onDeleteRow}
      onDuplicateRow={onDuplicateRow}
      onCopyRow={onCopyRow}
      onDuplicateGroup={onDuplicateGroup}
      onDeleteGroupRows={onDeleteGroupRows}
      onUndo={undo.undo}
      onRedo={undo.redo}
      canUndo={undo.canUndo}
      canRedo={undo.canRedo}
      openRowId={openRowId}
      openCommentId={openCommentId}
      openCommentQuery={openCommentQuery}
      onDetailChange={setOpenRow}
      onCommentConsumed={clearComment}
      focusRowId={focusRowId}
      onFocusConsumed={() => setFocusRowId(null)}
    />
  );
}

function BoardPageInner({
  board,
  rawBoard,
  view,
  setView,
  onPatchRow,
  onAddRow,
  onPatchCell,
  onAddColumnOption,
  onRowPositionChange,
  onMoveRow,
  onColumnUpdate,
  onAddSubitem,
  onCreateSubtask,
  onDeleteRow,
  onDuplicateRow,
  onCopyRow,
  onDuplicateGroup,
  onDeleteGroupRows,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  openRowId,
  openCommentId,
  openCommentQuery,
  onDetailChange,
  onCommentConsumed,
  focusRowId = null,
  onFocusConsumed,
}: {
  board: ViewBoard;
  rawBoard: BoardDetail | null;
  view: BoardView;
  setView: (next: BoardView) => void;
  onPatchRow: (id: string, patch: Partial<ViewRow>) => void;
  onAddRow?: (afterRowId?: UUID | null, inGroup?: { columnId: UUID; value: CellValue }) => void;
  onPatchCell?: (rowId: string, columnDefId: string, value: CellValue) => void;
  onAddColumnOption?: (
    column: ColumnDef,
    label: string,
  ) => Promise<{ id: string; label: string; color: string } | null>;
  onRowPositionChange?: (rowId: UUID, position: number) => void;
  onMoveRow?: (rowId: UUID, parentRowId: UUID | null, position: number) => void;
  onColumnUpdate?: (columnId: UUID, payload: ColumnUpdatePayload) => void;
  onAddSubitem?: (parentRowId: UUID) => void;
  onCreateSubtask?: (parentRowId: UUID, title: string, assigneeId: string | null) => void;
  onDeleteRow?: (rowId: UUID) => void;
  onDuplicateRow?: (rowId: UUID, withComments: boolean) => void;
  onCopyRow?: (rowId: UUID, opts: CopyRowOpts) => void;
  onDuplicateGroup?: (
    groupColumnId: string,
    sourceValue: unknown,
    newLabel: string,
    withComments: boolean,
  ) => void;
  onDeleteGroupRows?: (groupColumnId: UUID, optionId: string, rowIds: UUID[]) => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  openRowId: string | null;
  openCommentId: string | null;
  openCommentQuery: string | null;
  onDetailChange: (rowId: string | null) => void;
  onCommentConsumed: () => void;
  focusRowId?: string | null;
  onFocusConsumed?: () => void;
}) {
  const wsQuery = useQuery({
    queryKey: queryKeys.workspaces,
    queryFn: api.listWorkspaces,
    enabled: Boolean(rawBoard),
  });
  const ws = rawBoard ? (wsQuery.data?.find((w) => w.id === rawBoard.workspace_id) ?? null) : null;
  // Board-admin gate for status/tags "+ Neue Option" inline-create.
  const { user } = useAuth();
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const { data: members = [] } = useWorkspaceMembers(
    rawBoard ? (rawBoard.workspace_id as UUID) : undefined,
  );
  const canAddColumnOption = useMemo(() => {
    if (!user?.email) return false;
    // Personal boards live in the membership-less "Persönlich" workspace; only
    // their creator can open one, so being here means you own it — allow option
    // management (the membership/role check below would always fail otherwise).
    if (ws?.is_personal) return true;
    if (members.length === 0) return false;
    const me = members.find((m) => m.email === user.email);
    return me?.role === "owner" || me?.role === "admin";
  }, [ws?.is_personal, members, user?.email]);

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        padding: 16,
        display: "flex",
        flexDirection: "column",
        background: "var(--bg)",
      }}
    >
      {rawBoard ? (
        <div style={{ padding: "0 2px 10px" }}>
          <Breadcrumb
            items={[
              { label: t.navHome, to: "/" },
              ws
                ? { label: ws.name, to: "/w/$workspaceId", params: { workspaceId: ws.id } }
                : { label: t.loading },
              { label: board.name },
            ]}
          />
        </div>
      ) : null}
      <BoardSurface
        board={board}
        view={view}
        setView={setView}
        onPatchRow={onPatchRow}
        onAddRow={onAddRow}
        onPatchCell={onPatchCell}
        onAddColumnOption={onAddColumnOption}
        canAddColumnOption={canAddColumnOption}
        onRowPositionChange={onRowPositionChange}
        onMoveRow={onMoveRow}
        onColumnUpdate={onColumnUpdate}
        onAddSubitem={onAddSubitem}
        onCreateSubtask={onCreateSubtask}
        onDeleteRow={onDeleteRow}
        onDuplicateRow={onDuplicateRow}
        onCopyRow={onCopyRow}
        onDuplicateGroup={onDuplicateGroup}
        onDeleteGroupRows={onDeleteGroupRows}
        onUndo={onUndo}
        onRedo={onRedo}
        canUndo={canUndo}
        canRedo={canRedo}
        onNewTicket={rawBoard?.can_write === false ? undefined : () => setNewTicketOpen(true)}
        boardId={rawBoard?.id ?? null}
        workspaceId={rawBoard?.workspace_id ?? null}
        openRowId={openRowId}
        openCommentId={openCommentId}
        openCommentQuery={openCommentQuery}
        onDetailChange={onDetailChange}
        onCommentConsumed={onCommentConsumed}
        focusRowId={focusRowId}
        onFocusConsumed={onFocusConsumed}
      />
      <NewTicketDialog open={newTicketOpen} onOpenChange={setNewTicketOpen} board={rawBoard} />
    </div>
  );
}
